import { useState } from 'react';
import { toast } from 'react-toastify';
import BillingContactNotificationChoices from 'components/billingContacts/BillingContactNotificationChoices';
import BillingMemberSummary from 'components/billingContacts/BillingMemberSummary';
import { isBillingRecipient } from 'components/billingContacts/billingContactForm';
import Button from 'components/button';
import ModalBody from 'components/modal/components/body';
import ModalFooter from 'components/modal/components/footer';
import Modalheader from 'components/modal/components/header';
import useModal from 'components/modal/useModal';
import { BILLING_CONTACTS_UPDATED } from 'components/SlideOver/dataSlideOver/data/slideOverCompany/CompanyBillingNotificationsCard';
import UseContext from 'hooks/useContext';
import ApiDatabase from 'server';
import { readBillingContactError } from './billingContactModal';

const toLevel = (checked) => (checked ? 'all' : 'none');
const BILLING_TYPES = ['invoices', 'reminders'];

/**
 * A member as a billing recipient: mockup 8.4 when picked from the search
 * (`isEdit` false), its edit from the card's pencil otherwise. The boxes are
 * the member's invoices and reminders notifications themselves, read from the
 * list and written through the member route (1306), as the contact sheet does.
 */
const BillingMemberView = ({ idCompany, member, title, isEdit, onBack }) => {
  const { toggle } = useModal();
  const { language, setModalDataReturn } = UseContext();
  const text = (key) => language.getValue(`companyBillingContacts.${key}`, '');
  // The member's levels as the list read them: what a save is compared to.
  const current = {
    invoices: member?.invoices === true,
    reminders: member?.reminders === true,
  };
  // Picked from the search, the member is added to receive something: both
  // boxes start checked, as for a new contact without an account (8.5).
  const initial = isEdit ? current : { invoices: true, reminders: true };
  const [choices, setChoices] = useState(initial);
  const [error, setError] = useState('');
  const [isSending, setIsSending] = useState(false);

  // Only the levels that move: the route takes a partial patch, and the list
  // may be older than a change made since on the contact sheet.
  const levelsToSend = (wanted) =>
    Object.fromEntries(
      BILLING_TYPES.filter((type) => wanted[type] !== current[type]).map(
        (type) => [type, toLevel(wanted[type])],
      ),
    );

  const hasChecked = choices.invoices || choices.reminders;
  const hasChanges = Object.keys(levelsToSend(choices)).length > 0;
  const canSave = !isSending && hasChanges && (isEdit || hasChecked);

  const changeChoice = (field, checked) => {
    setError('');
    setChoices((previous) => ({ ...previous, [field]: checked }));
  };

  const send = (wanted, toastKey) => {
    setError('');
    setIsSending(true);
    ApiDatabase.setAdminMemberNotifications(
      {
        idCompany,
        idEmployee: member.idEmployee,
        levels: levelsToSend(wanted),
      },
      () => {
        setIsSending(false);
        toast.success(text(`toasts.${toastKey}`));
        setModalDataReturn({ type: BILLING_CONTACTS_UPDATED });
        toggle();
      },
      (failure) => {
        const refusal = readBillingContactError(failure);
        if (refusal.kind === 'canceled') return;
        setIsSending(false);
        if (refusal.kind === 'field') {
          setError(text(`errors.${refusal.key}`));
          return;
        }
        toast.error(text(`errors.${refusal.key}`));
        if (refusal.kind === 'gone') {
          setModalDataReturn({ type: BILLING_CONTACTS_UPDATED });
          toggle();
        }
      },
    );
  };

  const save = () => {
    if (!canSave) return;
    // Unchecking both boxes is the same as « Retirer »: said the same way.
    send(choices, isEdit && !hasChecked ? 'removed' : 'saved');
  };

  const remove = () => send({ invoices: false, reminders: false }, 'removed');

  return (
    <>
      <Modalheader hide={toggle} hideShow={!isSending}>
        {title}
      </Modalheader>
      <ModalBody>
        <div className={'flex flex-col gap-4'}>
          <BillingMemberSummary member={member} />
          <BillingContactNotificationChoices
            invoices={choices.invoices}
            reminders={choices.reminders}
            onChange={changeChoice}
            isDisabled={isSending}
            error={error}
          />
        </div>
      </ModalBody>
      <ModalFooter>
        {isEdit && isBillingRecipient(current) ? (
          <button
            type={'button'}
            disabled={isSending}
            onClick={remove}
            className={
              'mr-auto text-sm text-red-600 underline underline-offset-4 disabled:text-gray-400'
            }
          >
            {text('member.remove')}
          </button>
        ) : null}
        <Button
          size={'LG'}
          color={isSending ? 'DISABLED' : 'SECONDARY'}
          disabled={isSending}
          onClick={isEdit ? toggle : onBack}
        >
          {text(isEdit ? 'actions.cancel' : 'actions.back')}
        </Button>
        <Button
          size={'LG'}
          color={canSave ? 'PRIMARY' : 'DISABLED'}
          disabled={!canSave}
          onClick={save}
        >
          {isSending ? text('actions.saving') : text('actions.save')}
        </Button>
      </ModalFooter>
    </>
  );
};

export default BillingMemberView;
